/**
 * Shared constants for the watchlist feature.
 *
 * Why: the `WatchlistListMyUserWatchlists` query is consumed from ~14 call
 * sites. Apollo's InMemoryCache keys `listMyUserWatchlists` by its full
 * argument set (no custom `keyArgs` is configured in `providers/ApolloClient`),
 * so a `first: 40` call and a `first: 100` call normalize to two distinct
 * cache entries. That fragmentation forces redundant network refetches and
 * lets components observe stale, divergent copies of the same list. Pinning
 * every call site to a single page size collapses them onto one cache key.
 */

/**
 * Page size for the `WatchlistListMyUserWatchlists` query. Every call site must
 * use this value so they share a single normalized Apollo cache entry.
 */
export const MY_WATCHLISTS_PAGE_SIZE = 100;

/**
 * The roles whose holders may change a watchlist's player membership.
 *
 * SE-8306: the backend gates BOTH `addPlayerToWatchlist` and
 * `removePlayerFromWatchlist` on this set, inside `lockWatchlistAggregate`, so
 * a VIEWER's write is refused in both directions (and refused as a 404, which
 * is SE-8305's problem, not this module's). Any membership affordance the UI
 * offers outside this set is an action that cannot succeed.
 *
 * Wire values rather than the generated `UserWatchlistRole` enum object: this
 * module is evaluated at import time by call sites whose suites partially mock
 * `@/generated/graphql`, and reading a missing enum member there would crash on
 * load.
 */
export const WATCHLIST_MEMBERSHIP_WRITE_ROLES: ReadonlySet<string> = new Set([
  "OWNER",
  "EDITOR",
]);

/**
 * Whether a grant may add or remove players on the watchlist it is held over.
 * @param role - The signed-in user's role on the list
 * @returns True for OWNER / EDITOR; false for VIEWER.
 */
export const canEditWatchlistMembership = (role: string): boolean =>
  WATCHLIST_MEMBERSHIP_WRITE_ROLES.has(role);
